import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { plansAPI } from '../utils/api';
import { Check, Crown, Zap, MessageCircle, Headphones } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function Billing() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const [plans, setPlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchPlans();
  }, [isAuthenticated, navigate]);

  const fetchPlans = async () => {
    try {
      const [plansRes, currentRes] = await Promise.all([
        plansAPI.list(),
        plansAPI.getCurrentPlan(),
      ]);
      setPlans(plansRes.data.data || []);
      setCurrentPlan(currentRes.data.data);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center">
          <div className="text-center space-y-4">
            <div className="w-12 h-12 border-4 border-[#2D3748] border-t-[#128C7E] rounded-full animate-spin mx-auto"></div>
            <p className="text-[#A0AEC0]">{t('Loading billing information...')}</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-[#0B0E14] p-4 sm:p-6 md:p-8">
        {/* Header Section */}
        <div dir={isRTL ? 'rtl' : 'ltr'} className="mb-10 sm:mb-16">
          <div className="max-w-3xl">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2 sm:mb-3">
              💳 {t('Billing & Plans')}
            </h1>
            <p className="text-xs sm:text-base md:text-lg text-[#A0AEC0]">{t('Choose the perfect plan for your needs')}</p>
          </div>
        </div>

        {/* Plans Grid - Enhanced Design */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 mb-10 sm:mb-16">
          {plans.map((plan, index) => {
            const isCurrent = currentPlan?.id === plan.id;
            const isPopular = plan.name?.toLowerCase().includes('pro') || plan.name?.toLowerCase().includes('premium');
            
            return (
              <div
                key={plan.id}
                className={`relative group transition-all duration-300 ${
                  isPopular && !isCurrent ? 'lg:scale-105' : ''
                }`}
              >
                {/* Gradient Background for Popular Plans */}
                {isPopular && !isCurrent && (
                  <div className="absolute inset-0 bg-gradient-to-br from-[#128C7E]/20 to-transparent rounded-2xl blur-xl opacity-50 group-hover:opacity-100 transition-opacity"></div>
                )}

                {/* Card */}
                <div
                  className={`relative h-full bg-gradient-to-br ${
                    isCurrent
                      ? 'from-[#1A1F2E] to-[#141820] border-2 border-[#128C7E] shadow-xl shadow-[#128C7E]/20'
                      : isPopular
                      ? 'from-[#1A1F2E] to-[#141820] border-2 border-[#128C7E]/60 shadow-lg shadow-[#128C7E]/10'
                      : 'from-[#1A1F2E] to-[#141820] border border-[#2D3748] hover:border-[#128C7E]/40 hover:shadow-lg hover:shadow-[#128C7E]/10'
                  } rounded-lg sm:rounded-2xl p-4 sm:p-6 md:p-8 backdrop-blur-sm overflow-hidden`}
                >
                  {/* Animated Border for Popular */}
                  {isPopular && !isCurrent && (
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#128C7E] to-transparent opacity-50"></div>
                  )}

                  {/* Badge */}
                  <div className="flex items-center justify-between mb-4 sm:mb-6">
                    <div>
                      <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-white flex items-center gap-2">
                        {plan.name}
                        {isPopular && <Crown size={20} className="sm:w-6 sm:h-6 text-[#128C7E]" />}
                      </h2>
                      {plan.description && (
                        <p className="text-[#718096] text-xs sm:text-sm mt-1">{plan.description}</p>
                      )}
                    </div>
                  </div>

                  {/* Status Badges */}
                  <div className="flex gap-2 mb-4 sm:mb-6 flex-wrap">
                    {isCurrent && (
                      <span className="bg-[#128C7E] text-white text-xs font-bold px-2 sm:px-3 py-1 rounded-full">
                        ✓ {t('Current')}
                      </span>
                    )}
                    {isPopular && !isCurrent && (
                      <span className="bg-[#128C7E]/20 border border-[#128C7E] text-[#128C7E] text-xs font-bold px-2 sm:px-3 py-1 rounded-full">
                        ⭐ {t('Popular')}
                      </span>
                    )}
                  </div>

                  {/* Pricing Section */}
                  <div className="mb-6 sm:mb-8 pb-6 sm:pb-8 border-b border-[#2D3748]">
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl sm:text-5xl font-bold text-white">
                        {plan.price_usd ? `$${plan.price_usd}` : t('Free')}
                      </span>
                      {plan.price_usd && <span className="text-[#A0AEC0] text-xs sm:text-base">/ {t('month')}</span>}
                    </div>
                    {plan.price_usd && (
                      <p className="text-[#718096] text-xs sm:text-sm mt-2">{t('Billed monthly')}</p>
                    )}
                  </div>

                  {/* Features List - Enhanced */}
                  <div className="space-y-2 sm:space-y-4 mb-6 sm:mb-8">
                    {/* Core Features */}
                    <div className="space-y-2 sm:space-y-3">
                      <div className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 bg-[#0B0E14]/50 rounded-lg hover:bg-[#0B0E14] transition-colors">
                        <Zap size={18} className="sm:w-5 sm:h-5 text-[#128C7E] flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium text-xs sm:text-sm">{t('OTP Requests')}</p>
                          <p className="text-[#A0AEC0] text-xs">{plan.otp_limit_monthly?.toLocaleString()} {t('per month')}</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3 p-3 bg-[#0B0E14]/50 rounded-lg hover:bg-[#0B0E14] transition-colors">
                        <MessageCircle size={20} className="text-[#128C7E] flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-white font-medium text-sm">{t('API Keys')}</p>
                          <p className="text-[#A0AEC0] text-xs">{plan.api_keys_limit} {t('keys available')}</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3 p-3 bg-[#0B0E14]/50 rounded-lg hover:bg-[#0B0E14] transition-colors">
                        <Headphones size={20} className="text-[#128C7E] flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-white font-medium text-sm">{t('Support')}</p>
                          <p className="text-[#A0AEC0] text-xs capitalize">{plan.support_level} {t('support')}</p>
                        </div>
                      </div>
                    </div>

                    {/* Additional Features */}
                    {(plan.features?.api || plan.features?.dashboard || plan.features?.analytics || plan.features?.webhooks || plan.features?.sso) && (
                      <div className="pt-3 border-t border-[#2D3748] space-y-2">
                        {plan.features?.api && (
                          <div className="flex items-center gap-2 text-[#A0AEC0] text-sm">
                            <Check size={16} className="text-[#128C7E] flex-shrink-0" />
                            <span>{t('REST API access')}</span>
                          </div>
                        )}
                        {plan.features?.dashboard && (
                          <div className="flex items-center gap-2 text-[#A0AEC0] text-sm">
                            <Check size={16} className="text-[#128C7E] flex-shrink-0" />
                            <span>{t('Dashboard access')}</span>
                          </div>
                        )}
                        {plan.features?.analytics && (
                          <div className="flex items-center gap-2 text-[#A0AEC0] text-sm">
                            <Check size={16} className="text-[#128C7E] flex-shrink-0" />
                            <span>{t('Advanced analytics')}</span>
                          </div>
                        )}
                        {plan.features?.webhooks && (
                          <div className="flex items-center gap-2 text-[#A0AEC0] text-sm">
                            <Check size={16} className="text-[#128C7E] flex-shrink-0" />
                            <span>{t('Webhook support')}</span>
                          </div>
                        )}
                        {plan.features?.sso && (
                          <div className="flex items-center gap-2 text-[#A0AEC0] text-sm">
                            <Check size={16} className="text-[#128C7E] flex-shrink-0" />
                            <span>{t('SSO integration')}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* CTA Button */}
                  {isCurrent ? (
                    <button className="w-full py-2 sm:py-3 bg-[#128C7E]/20 border border-[#128C7E] text-[#128C7E] rounded-lg font-semibold text-sm sm:text-base transition-all duration-300 cursor-default hover:bg-[#128C7E]/30">
                      ✓ {t('Current Plan')}
                    </button>
                  ) : (
                    <button
                      className="w-full py-2 sm:py-3 bg-[#128C7E] text-white rounded-lg font-semibold text-sm sm:text-base hover:bg-[#0DA699] transition-all duration-300 hover:shadow-lg hover:shadow-[#128C7E]/30 active:scale-95"
                      onClick={() => {
                        const lang = localStorage.getItem('locale') || 'en';
                        const message =
                          lang === 'ar'
                            ? `أريد الاشتراك في خطة ${plan.name}`
                            : `I want to subscribe to the ${plan.name} plan`;
                        const url = `https://wa.me/962771958387?text=${encodeURIComponent(message)}`;
                        window.open(url, '_blank');
                      }}
                    >
                      {t('Upgrade')}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Comparison Table Section */}
        <div className="mb-10 sm:mb-16">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6 sm:mb-8">{t('Compare Plans')}</h2>
          <div className="bg-[#1A1F2E] border border-[#2D3748] rounded-lg sm:rounded-2xl overflow-hidden shadow-xl">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#2D3748] bg-[#0B0E14]/50">
                    <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-[#A0AEC0] font-semibold text-xs sm:text-base">{t('Feature')}</th>
                    {plans.map((plan) => (
                      <th key={plan.id} className="px-3 sm:px-6 py-3 sm:py-4 text-center text-white font-semibold text-xs sm:text-base">
                        {plan.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#2D3748]">
                  <tr className="hover:bg-[#0B0E14]/30 transition-colors">
                    <td className="px-6 py-4 text-[#A0AEC0]">{t('Price')}</td>
                    {plans.map((plan) => (
                      <td key={plan.id} className="px-6 py-4 text-center text-white font-semibold">
                        {plan.price_usd ? `$${plan.price_usd}/mo` : t('Free')}
                      </td>
                    ))}
                  </tr>
                  <tr className="hover:bg-[#0B0E14]/30 transition-colors">
                    <td className="px-6 py-4 text-[#A0AEC0]">{t('OTP Requests/Month')}</td>
                    {plans.map((plan) => (
                      <td key={plan.id} className="px-6 py-4 text-center text-white">
                        {plan.otp_limit_monthly?.toLocaleString()}
                      </td>
                    ))}
                  </tr>
                  <tr className="hover:bg-[#0B0E14]/30 transition-colors">
                    <td className="px-6 py-4 text-[#A0AEC0]">{t('API Keys')}</td>
                    {plans.map((plan) => (
                      <td key={plan.id} className="px-6 py-4 text-center text-white">
                        {plan.api_keys_limit}
                      </td>
                    ))}
                  </tr>
                  <tr className="hover:bg-[#0B0E14]/30 transition-colors">
                    <td className="px-6 py-4 text-[#A0AEC0]">{t('Support Level')}</td>
                    {plans.map((plan) => (
                      <td key={plan.id} className="px-6 py-4 text-center">
                        <span className="px-3 py-1 bg-[#128C7E]/20 text-[#128C7E] rounded-full text-sm font-medium capitalize">
                          {plan.support_level}
                        </span>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-gradient-to-br from-[#1A1F2E] to-[#141820] border border-[#2D3748] rounded-2xl p-8 md:p-10">
          <h2 className="text-3xl font-bold text-white mb-8">❓ {t('Frequently Asked Questions')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-3 p-6 bg-[#0B0E14]/30 rounded-lg hover:bg-[#0B0E14]/50 transition-colors">
              <h3 className="text-lg font-semibold text-white">{t('Can I change my plan anytime?')}</h3>
              <p className="text-[#A0AEC0]">{t('Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately.')}</p>
            </div>
            <div className="space-y-3 p-6 bg-[#0B0E14]/30 rounded-lg hover:bg-[#0B0E14]/50 transition-colors">
              <h3 className="text-lg font-semibold text-white">{t('What happens if I exceed my OTP limit?')}</h3>
              <p className="text-[#A0AEC0]">{t("If you exceed your monthly limit, we'll notify you. You can upgrade your plan to increase the limit.")}</p>
            </div>
            <div className="space-y-3 p-6 bg-[#0B0E14]/30 rounded-lg hover:bg-[#0B0E14]/50 transition-colors">
              <h3 className="text-lg font-semibold text-white">{t('Do you offer annual billing?')}</h3>
              <p className="text-[#A0AEC0]">{t('Yes! Contact our sales team for annual billing discounts.')}</p>
            </div>
            <div className="space-y-3 p-6 bg-[#0B0E14]/30 rounded-lg hover:bg-[#0B0E14]/50 transition-colors">
              <h3 className="text-lg font-semibold text-white">{t('Is there a free trial?')}</h3>
              <p className="text-[#A0AEC0]">{t("Yes! Start with our free Starter plan and upgrade whenever you're ready.")}</p>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
